import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import usePantallaEdit from "hooks/usePantallaEdit";
import { useRequestTracker } from "hooks/useResquestTracker";
import { VendedorManager } from "managers";
import { GridView, SvgComponent } from "components";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { Simplebar } from "components/Simplebar/Simplebar";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import Select from "react-select";
import { Vendedor } from "models/ECF/Vendedor";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import SuspenseECF from "components/Suspense/SuspenseECF";
const Exportar = lazy(() => import("components/Exportar/Exportar"));
const VendedorEdit = lazy(() => import("./VendedorEdit"));

const VendedorList = ({ showLoading, hideLoading, hideTitle, isActive }) => {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const [showCanvas, setShowCanvas] = useState(false);
  const [vendedor, setVendedorState] = useState();
  const { Message, cerrarAlertas } = useMessage();
  const ref = useRef(null);
  const [showExportar, setShowExportar] = useState(false);
  const { trackRequest } = useRequestTracker();
  const ESTATUS_LIST = [
    { value: "", label: "Todos" },
    ...Vendedor.ESTATUS_LIST,
  ];
  const {
    setEntidad: setVendedor,
    setEstadoCargado,
    setHayCambios,
  } = usePantallaEdit(setVendedorState);

  useEffect(() => {
    if (isActive) {
      buscar();
    }
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where, isActive]);

  useEffect(() => {
    if (vendedor != undefined) {
      setShowCanvas(true);
    }
  }, [vendedor]);

  const buscar = () => {
    const whereWithPaginationValues = { ...where, ...pagination };

    trackRequest({
      executeRequest: (signal) =>
        VendedorManager.listado(whereWithPaginationValues, {
          signal,
        }),
      onSuccess: (res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        setList(res.Data);
      },
      onError: (error) => handleException(error),
      onFinally: () => hideLoading(),
    });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const handleClick = (id) => {
    setEstadoCargado(false);
    if (id) {
      VendedorManager.buscarPorId(id)
        .then((data) => {
          setVendedor(data);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          hideLoading();
          setEstadoCargado(true);
        });
    } else {
      setVendedor({});
      setEstadoCargado(true);
    }
  };

  const grabar = () => {
    ref.current?.showLoading(true);
    if (!vendedor.Img) {
      vendedor.Img = [];
    }

    if (!vendedor.IdSec) {
      VendedorManager.crear(vendedor)
        .then((res) => {
          Message({ message: `Grabado Exitosamente` });
          buscar();
          setVendedor(res);
          setShowCanvas(false);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          ref.current?.showLoading(false);
          setHayCambios(false);
        });
    } else {
      VendedorManager.actualizar(vendedor)
        .then(() => {
          buscar();
          Message({ message: `Actualizado Exitosamente` });
          setShowCanvas(false);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          ref.current?.showLoading(false);
          setHayCambios(false);
        });
    }
  };

  const vendedorEdit = (
    <Simplebar
      ref={ref}
      backdrop={"static"}
      confirmBack
      placement={"end"}
      portal={false}
      visible={showCanvas}
      setShow={(e) => {
        setShowCanvas(e);
      }}
      hideCallback={() => {
        setEstadoCargado(false);
      }}
      useLoading={true}
      title={
        vendedor?.IdSec || false ? "Actualizar Vendedor" : "Crear Vendedor"
      }
      btnLeft={{
        show: true,
        message: "Grabar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "save",
        isCenter: true,
        func: grabar,
      }}
      btnRight={{
        show: false,
        label: "Grabar",
        func: () => { },
      }}
    >
      <VendedorEdit vendedor={vendedor} setVendedor={setVendedor} />
    </Simplebar>
  );

  const openExportar = () => {
    setShowExportar(true);
  };

  return (
    <div>
      {showExportar && (
        <Suspense fallback={<SuspenseECF />}>
          <Exportar
            PROPIEDADES_EXPORTAR={Vendedor.PROPIEDADES_EXPORTAR}
            showExportar={showExportar}
            setShowExportar={setShowExportar}
            showLoading={showLoading}
            hideLoading={hideLoading}
            where={where}
            exportarCallBack={VendedorManager.exportar}
            serialize={true}
          ></Exportar>
        </Suspense>
      )}

      <div style={{ paddingRight: "6px" }}>
        <ListHeader
          titulo={hideTitle ? "" : "Vendedor"}
          crearNuevo={() => handleClick()}
          top="0px"
          exportar={openExportar}
        ></ListHeader>
      </div>
      {showCanvas && (
        <Suspense fallback={<SuspenseECF />}>{vendedorEdit}</Suspense>
      )}
      <>
        <GridView
          pagination={pagination}
          setPagination={setPagination}
          allowPaging={pagination.EsPaginable}
          pageSize={pagination.CantidadPorPagina}
          pageIndex={pagination.IndiceDePagina}
          lastPageNumber={pagination.PaginasTotal}
          dataSource={list}
          onPageIndexChanging={onPageIndexChanging}
          where={where}
          setWhere={setWhere}
          height="70vh"
        >
          <GridView.Column
            isSticky
            bodyRenderer={(row) => {
              return (
                <>
                  <div className="float-right">
                    <IconButton onClick={() => handleClick(row.IdSec)}>
                      <ToolTip message="Editar">
                        <div>
                          <SvgComponent
                            iconName="pencil"
                            height="25"
                            width="25"
                          />
                        </div>
                      </ToolTip>
                    </IconButton>
                  </div>
                </>
              );
            }}
          />
          <GridView.Column
            headerText="Id"
            dataField="IdSec"
            sortExpression="IdSec"
            width="100px"
          />
          <GridView.Column
            headerText="Nombre"
            dataField="Nombre"
            sortExpression="Nombre"
          />
          <GridView.Column
            headerText="Estatus"
            dataField="Estatus"
            sortExpression="Estatus"
            filterRenderer={() => {
              return (
                <Select
                  className="react-select primary"
                  options={ESTATUS_LIST}
                  value={ESTATUS_LIST.filter(function (option) {
                    return (
                      option.value ===
                      (where.Estatus ? where.Estatus : ESTATUS_LIST[0].value)
                    );
                  })}
                  name="Estatus"
                  onChange={(e) =>
                    setWhere({
                      ...where,
                      Estatus: e.value,
                    })
                  }
                ></Select>
              );
            }}
          />
        </GridView>
      </>
    </div>
  );
};

export default VendedorList;
